/**
 * Rango de fechas + comparación de períodos (Fase FF1). Base reutilizable del filtro por fechas que
 * usarán el Dashboard y todas las secciones. Toda la lógica de calendario vive aquí (pura y testeable):
 * resolver presets ("este mes", "últimos 7 días"…), calcular el PERÍODO ANTERIOR equivalente para
 * comparar, y el % de variación. La zona horaria es la local del equipo (Chile).
 *
 * Convención: un rango es [fromEpoch, toEpoch] con AMBOS extremos inclusivos, en milisegundos epoch.
 * `fromEpoch` = 00:00:00.000 del primer día; `toEpoch` = 23:59:59.999 del último día.
 */
export type PresetId = "today" | "yesterday" | "last7" | "last30" | "thisMonth" | "lastMonth" | "custom";

export interface DateRange { readonly fromEpoch: number; readonly toEpoch: number }

const DAY = 86_400_000;

/** Inicio del día (00:00:00.000 local) de una fecha. */
export function startOfDay(d: Date): number { return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime(); }
/** Fin del día (23:59:59.999 local). */
export function endOfDay(d: Date): number { return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime(); }

/** Rango de un día calendario completo a partir de un epoch. */
function dayRange(epoch: number): DateRange { const d = new Date(epoch); return { fromEpoch: startOfDay(d), toEpoch: endOfDay(d) }; }

/** Resuelve un preset a un rango concreto, relativo a `now`. `custom` cae al rango que se le pase. */
export function resolvePreset(preset: PresetId, now: number = Date.now(), custom?: DateRange): DateRange {
  const nd = new Date(now);
  switch (preset) {
    case "today": return dayRange(now);
    case "yesterday": return dayRange(now - DAY);
    case "last7": return { fromEpoch: startOfDay(new Date(now - 6 * DAY)), toEpoch: endOfDay(nd) };   // hoy + 6 previos = 7 días
    case "last30": return { fromEpoch: startOfDay(new Date(now - 29 * DAY)), toEpoch: endOfDay(nd) };
    case "thisMonth": return { fromEpoch: new Date(nd.getFullYear(), nd.getMonth(), 1, 0, 0, 0, 0).getTime(), toEpoch: endOfDay(nd) };
    case "lastMonth": {
      const firstThis = new Date(nd.getFullYear(), nd.getMonth(), 1, 0, 0, 0, 0);
      const lastPrev = new Date(firstThis.getTime() - DAY);
      return { fromEpoch: new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1, 0, 0, 0, 0).getTime(), toEpoch: endOfDay(lastPrev) };
    }
    case "custom": return custom ?? dayRange(now);
  }
}

/** Duración del rango en días calendario (redondeada, mínimo 1). */
export function rangeDays(r: DateRange): number { return Math.max(1, Math.round((r.toEpoch - r.fromEpoch) / DAY)); }

/**
 * Período ANTERIOR equivalente: mismo largo, inmediatamente antes del rango dado. Para "este mes" y
 * "mes pasado" usa el MES calendario anterior (respeta meses de distinto largo); para el resto, desplaza
 * el rango tantos días como dure.
 */
export function previousPeriod(r: DateRange, preset?: PresetId): DateRange {
  if (preset === "thisMonth" || preset === "lastMonth") {
    const from = new Date(r.fromEpoch);
    const prevMonthLastDay = new Date(from.getFullYear(), from.getMonth(), 0); // día 0 = último del mes anterior
    const prevMonthFirst = new Date(prevMonthLastDay.getFullYear(), prevMonthLastDay.getMonth(), 1, 0, 0, 0, 0);
    return { fromEpoch: prevMonthFirst.getTime(), toEpoch: endOfDay(prevMonthLastDay) };
  }
  const days = rangeDays(r);
  const shift = days * DAY;
  return { fromEpoch: r.fromEpoch - shift, toEpoch: r.toEpoch - shift };
}

export interface Comparison { readonly deltaPct: number | null; readonly direction: "up" | "down" | "flat" }

/** Variación % de `current` respecto de `previous`. deltaPct = null si el anterior era 0 (no comparable). */
export function comparePeriods(current: number, previous: number): Comparison {
  if (previous === 0) return { deltaPct: current === 0 ? 0 : null, direction: current > 0 ? "up" : "flat" };
  const deltaPct = Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
  return { deltaPct, direction: deltaPct > 0 ? "up" : deltaPct < 0 ? "down" : "flat" };
}

/** ¿El epoch cae dentro del rango (inclusive)? Útil para filtrar listas en el cliente. */
export function inRange(epoch: number, r: DateRange): boolean { return epoch >= r.fromEpoch && epoch <= r.toEpoch; }

export const PRESET_LABEL: Record<PresetId, string> = {
  today: "Hoy", yesterday: "Ayer", last7: "Últimos 7 días", last30: "Últimos 30 días",
  thisMonth: "Este mes", lastMonth: "Mes pasado", custom: "Personalizado",
};

/** Etiqueta legible de un rango, ej. "1 sep – 15 sep 2026". */
export function formatRange(r: DateRange): string {
  const f = new Date(r.fromEpoch), t = new Date(r.toEpoch);
  const fmt = (d: Date): string => d.toLocaleDateString("es-CL", { day: "numeric", month: "short" });
  const sameDay = startOfDay(f) === startOfDay(t);
  return sameDay ? `${fmt(f)} ${t.getFullYear()}` : `${fmt(f)} – ${fmt(t)} ${t.getFullYear()}`;
}
